import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, UserPlus, Briefcase, Users, DollarSign, TrendingUp, Download, Smartphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function MobileDashboard() {
  const navigate = useNavigate();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();

  const { data: stats } = useQuery({
    queryKey: ["mobile-dashboard-stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) throw new Error("No organization");

      const [jobsData, leadsData] = await Promise.all([
        supabase.from("jobs").select("estimated_value", { count: "exact" }).eq("org_id", profile.org_id),
        supabase.from("leads").select("*", { count: "exact" }).eq("org_id", profile.org_id),
      ]);

      const revenue = jobsData.data?.reduce((sum, job) => sum + (job.estimated_value || 0), 0) || 0;

      return {
        jobs: jobsData.count || 0,
        leads: leadsData.count || 0,
        revenue,
      };
    },
  });

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      toast.success("App installed successfully!");
    } else {
      // iOS or already installed - show instructions
      toast.info("On iOS: Tap Share → Add to Home Screen");
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-4 space-y-4 pb-28">
      {/* Welcome Section */}
      <div className="mb-2">
        <h1 className="text-2xl font-bold truncate">Welcome back 👋</h1>
        <p className="text-sm text-muted-foreground truncate">Capture leads and inspect roofs</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          onClick={() => navigate("/field-capture")}
          className="h-24 flex flex-col gap-1.5 shadow-lg flex-shrink-0"
          size="lg"
        >
          <Camera className="h-6 w-6" />
          <span className="text-xs font-semibold truncate w-full px-1">Field Scanner</span>
        </Button>
        <Button
          onClick={() => navigate("/leads?action=create")}
          variant="outline"
          className="h-24 flex flex-col gap-1.5 shadow-md flex-shrink-0"
        >
          <UserPlus className="h-6 w-6" />
          <span className="text-xs font-semibold truncate w-full px-1">New Lead</span>
        </Button>
      </div>

      {/* Install App Button - Only show if not installed */}
      {!isInstalled && (
        <Card 
          className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 cursor-pointer hover:shadow-md transition-shadow"
          onClick={handleInstall}
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              {isInstallable ? <Download className="h-5 w-5 text-primary" /> : <Smartphone className="h-5 w-5 text-primary" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">Install App</p>
              <p className="text-xs text-muted-foreground truncate">
                {isInstallable ? "Add to home screen" : "iOS: Share → Add to Home Screen"}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Stats - 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3 text-center hover:shadow-md transition-shadow flex-shrink-0">
          <Briefcase className="h-5 w-5 mx-auto mb-1.5 text-primary" />
          <div className="text-xl font-bold truncate">{stats?.jobs || 0}</div>
          <div className="text-xs text-muted-foreground truncate">Active Jobs</div>
        </Card>
        <Card className="p-3 text-center hover:shadow-md transition-shadow flex-shrink-0">
          <Users className="h-5 w-5 mx-auto mb-1.5 text-primary" />
          <div className="text-xl font-bold truncate">{stats?.leads || 0}</div>
          <div className="text-xs text-muted-foreground truncate">Hot Leads</div>
        </Card>
        <Card className="p-3 text-center hover:shadow-md transition-shadow flex-shrink-0 col-span-2">
          <DollarSign className="h-5 w-5 mx-auto mb-1.5 text-primary" />
          <div className="text-xl font-bold truncate">${((stats?.revenue || 0) / 1000).toFixed(0)}k</div>
          <div className="text-xs text-muted-foreground truncate">Total Pipeline Value</div>
        </Card>
      </div>

      {/* Pipeline Overview */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold truncate">Pipeline Overview</h3>
          <TrendingUp className="h-4 w-4 text-primary flex-shrink-0" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
          {[
            { stage: "New Leads", count: 0, color: "bg-blue-500" },
            { stage: "Quoted", count: 0, color: "bg-yellow-500" },
            { stage: "In Progress", count: 0, color: "bg-orange-500" },
            { stage: "Completed", count: 0, color: "bg-green-500" },
          ].map((item) => (
            <Card 
              key={item.stage} 
              className="p-3 min-w-[120px] w-[120px] snap-start hover:shadow-md transition-shadow flex-shrink-0"
            >
              <div className={cn("h-1 w-10 rounded-full mb-2", item.color)} />
              <div className="text-xs font-medium text-muted-foreground truncate mb-1">{item.stage}</div>
              <div className="text-2xl font-bold">{item.count}</div>
            </Card>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate("/jobs")}
          className="truncate"
        >
          View All Jobs
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate("/leads")}
          className="truncate"
        >
          View All Leads
        </Button>
      </div>
    </div>
  );
}
